import { InformationSource } from "@/types/information-source/information-source.types";
import { Column } from "@/components/ui/data-table/types";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Pencil, Archive, ArchiveRestore, ExternalLink } from "lucide-react";
import { ensureProtocol } from "@/utils/urlFormatter";
import { useAuth } from "@/contexts/AuthContext";

export const useInfoSourceColumns = (
  t: (key: string) => string,
  onEdit: (source: InformationSource) => void,
  onArchiveAction: (source: InformationSource) => void
): Column<InformationSource>[] => {
  const { claims } = useAuth();
  const isAssimagraUser = claims?.role === "assimagra";
  
  const baseColumns: Column<InformationSource>[] = [
    {
      header: t("infoSources.table.name"),
      accessorKey: "name",
      sortable: true,
      className: "min-w-[180px]",
    },
    {
      header: t("infoSources.table.year"),
      accessorKey: "year",
      sortable: true,
      className: "min-w-[120px]",
      cell: (_: string, row: InformationSource) => <div>{row.year?.toString() || ""}</div>,
    },
    {
      header: t("infoSources.table.link"),
      accessorKey: "link",
      sortable: false,
      className: "min-w-[140px]",
      cell: (_: string, row: InformationSource) => (
        <div>
          {row.link && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => window.open(ensureProtocol(row.link!), "_blank")}
                    className="h-8 w-8 hover:bg-gray-100"
                  >
                    <ExternalLink className="h-4 w-4 text-gray-500" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" align="center">
                  <p>{t("infoSources.actions.openLink")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </div>
      ),
    },
  ];

  const actionsColumn: Column<InformationSource> = {
    header: t("infoSources.table.actions"),
    accessorKey: "id",
    sortable: false,
    className: "w-[100px]",
    cell: (_value: string, row: InformationSource) => (
      <div className="flex gap-2 justify-end">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="icon" onClick={() => onEdit(row)} className="h-8 w-8 hover:bg-gray-100">
                <Pencil className="h-4 w-4 text-gray-500" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{t("infoSources.actions.edit")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onArchiveAction(row)}
                className="h-8 w-8 hover:bg-gray-100"
              >
                {!row.isArchived ? (
                  <Archive className="h-4 w-4 text-gray-500" />
                ) : (
                  <ArchiveRestore className="h-4 w-4 text-gray-500" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top" align="center">
              <p>{!row.isArchived ? t("infoSources.actions.archive") : t("infoSources.actions.restore")}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    ),
  };

  return isAssimagraUser ? baseColumns : [...baseColumns, actionsColumn];
};
